import { mergeDeepRight } from 'ramda';
import {
  ICountry,
  ICountry_Search_Radius,
  ICountry_Supplier,
} from '@bridebook/models/source/models/Countries.types';
import { env } from '@bridebook/toolbox/src/env';
import { IMAGES_BASE_URL } from '../buildImageUrl';
import GazetteerDev from './data/gazetteer.dev.json';
import GazetteerProd from './data/gazetteer.prod.json';
import { getTimeZones } from '@vvo/tzdb';
import { default as buckets } from './buckets';
import type {
  IBucket,
  SupplierTierSettings,
  SupplierTypesSettings,
  SupplierTypeOrWildcardSettings,
} from '@bridebook/models/source/models/Countries/Buckets.types';
import overrides from './buckets/overrides';
import { CountryCodes } from './countries';
import { isDefined, isObject, isSupplierType } from '../is';

export { CountryCodes } from './countries';

// Helper type to check if a setting has supplier type keys and return appropriate type
// Checks if the setting type has '*' as a key (indicating supplier type keys structure)
export type GetSettingReturnType<
  K extends keyof IBucket['settings'],
  T extends ICountry_Supplier | undefined,
> = T extends ICountry_Supplier
  ? NonNullable<IBucket['settings'][K]> extends { '*': infer V }
    ? V | null
    : NonNullable<IBucket['settings'][K]> extends { '*'?: infer V }
      ? V | null
      : IBucket['settings'][K]
  : IBucket['settings'][K];

/**
 * The default locale to use.
 */
export const defaultLocale: string = 'en';

/**
 * Alias token usable anywhere a supplier type is targeted in bucket settings. It stands for every
 * supplier type except the ones listed below, letting configs express "all non-venue suppliers"
 * without repeating the full list.
 */
export const SUPPLIER_TYPE_ALIAS = 'suppliers';

/**
 * Supplier types the `suppliers` alias deliberately leaves out.
 */
export const SUPPLIER_ALIAS_EXCLUDED_TYPES: ICountry_Supplier[] = ['venue', 'group'];

/**
 * Whether a supplier type is covered by the `suppliers` alias.
 */
const isSupplierAliasType = (type: ICountry_Supplier): boolean =>
  !SUPPLIER_ALIAS_EXCLUDED_TYPES.includes(type);

/**
 * Resolves whether an inclusion/exclusion rule matches a supplier type. A rule can be the `'*'`
 * wildcard, the `suppliers` alias, or an array that itself may contain specific types and/or the
 * alias.
 */
const typeRuleMatches = (rule: unknown, type: ICountry_Supplier): boolean => {
  if (rule === '*') {
    return true;
  }
  if (rule === SUPPLIER_TYPE_ALIAS) {
    return isSupplierAliasType(type);
  }
  if (Array.isArray(rule)) {
    return rule.some((entry) =>
      entry === SUPPLIER_TYPE_ALIAS ? isSupplierAliasType(type) : entry === type,
    );
  }
  return false;
};

/**
 * Merges a bucket's base settings with a country-specific override
 */
export const mergeBucketSettings = (
  base: IBucket['settings'],
  override: object,
): IBucket['settings'] => mergeDeepRight(base, override) as IBucket['settings'];

/**
 * This pattern matches any `country[-language[-locale]]` market prefix (unbounded).
 */
export const marketPrefixPattern = /[a-z]{2}(?:-[a-z]{2}){0,2}/;

/**
 * Cherry-picked from `ICountry` type for the purposes of the gazetteer.
 */
export type Country = {
  alias?: ICountry['alias'];
  areas: Required<ICountry>['areas'];
  bucket: ICountry['bucket'];
  config: ICountry['config'];
  continent: Required<ICountry>['continent'];
  currency: Required<ICountry>['currency'];
  flags: NonNullable<ICountry['flags']>;
  id: CountryCodes;
  locales: Required<ICountry>['locales'];
  mappings: ICountry['mappings'] & {
    search: {
      radius: ICountry_Search_Radius;
    };
  };
  suppliers: NonNullable<ICountry['suppliers']>;
};

type MarketKey = Lowercase<Country['id'] | NonNullable<Country['alias']>>;

export class Gazetteer {
  readonly #data: Partial<Record<MarketKey, Country>> = {};

  constructor(countries: Country[]) {
    const fallback = countries.find((country) => country.id === ('*' as CountryCodes)) ?? {};

    for (const country of countries) {
      if (country.id === ('*' as CountryCodes)) {
        continue;
      }

      /**
       * Prebuild country/alias market keys for faster lookups.
       */
      const keys: [MarketKey, MarketKey?] = [
        country.id.toLowerCase() as MarketKey,
        country.alias?.toLowerCase() as MarketKey | undefined,
      ];

      /**
       * Merge default values from fallback (`*`) country.
       */
      const value: Country = mergeDeepRight({ ...fallback }, country);

      /**
       * Make sure the all inherited locales are fully qualified.
       */
      value.locales = value.locales.map((value) => {
        if (value.locale.length === 2) {
          value.locale = `${value.locale}-${country.id.toUpperCase()}`;
        }

        return value;
      });

      /**
       * Assign the market definition to all valid map lookup keys.
       */
      for (const key of keys) {
        if (key == null) {
          continue;
        }

        this.#data[key] = value;
      }
    }
  }

  /**
   * Returns all countries that match the provided flags.
   *
   * @param flags Optional flags to filter by.
   * @returns All filtered countries.
   */
  getCountries(flags?: Partial<Country['flags']>): Country[] {
    const result = new Set<Country>();

    for (const country of Object.values(this.#data)) {
      result.add(country);
    }

    if (flags != null) {
      return [...result].filter((country) => {
        for (const [key, value] of Object.entries(flags)) {
          const index = key as keyof Country['flags'];

          if (country.flags[index] !== value) {
            return false;
          }
        }

        return true;
      });
    }

    return [...result];
  }

  /**
   * Returns all country codes that match the specified buckets
   *
   * @param bucketIds The bucket ids to filter by.
   * @returns All country codes in the specified buckets.
   */
  getCountryCodes(bucketIds: IBucket['id'][]): CountryCodes[] {
    const result = new Set<Country>();

    for (const country of Object.values(this.#data)) {
      result.add(country);
    }

    return [...result]
      .filter((country) => country.bucket?.id && bucketIds.includes(country.bucket.id))
      .map((country) => country.id);
  }

  /**
   * Returns an Emoji flag for the country.
   * Note that Emoji flags are not rendered correctly on Windows.
   *
   * @param country The country code.
   * @returns The emoji flag.
   */
  static getCountryFlagEmoji(country: CountryCodes | 'EU'): string {
    return country
      .split('')
      .map((value) => String.fromCodePoint(0x1f1a5 + value.charCodeAt(0)))
      .join('');
  }

  /**
   * Returns an URL to a flag image for the country.
   * The original files were downloaded from https://flagcdn.com/.
   *
   * @param country The country code.
   * @returns The URL to the flag image.
   */
  static getCountryFlagImageURL(country: CountryCodes | 'EU'): string | undefined {
    if (Object.keys(CountryCodes).concat('EU').includes(country) !== true) {
      return undefined;
    }

    return `${IMAGES_BASE_URL}/assets/flags/160x120/${country.toLowerCase()}.png`;
  }

  /**
   * Uses the `Intl` API to derive the country name from the country code.
   *
   * @param country The country code.
   * @param locale The locale to use for the country name.
   * @returns The country name.
   */
  static getCountryName(country: CountryCodes, locale = defaultLocale): string {
    return new Intl.DisplayNames(locale, { type: 'region' }).of(country) ?? '';
  }

  /**
   * Uses the `Intl` API to derive the currency name from the currency code.
   * This is useful for when we need to display the currency name in the UI.
   *
   * @param currency The currency code.
   * @param locale The locale to use for the currency name.
   * @returns The currency name.
   */
  static getCurrencyName(currency: string, locale = defaultLocale): string {
    const result = new Intl.DisplayNames(locale, { type: 'currency' }).of(currency) ?? '';
    const segments = [];

    /**
     * Intl.Segmenter is not supported in the current Firefox release.
     */
    if (Intl.Segmenter === undefined) {
      for (const segment of result.split(' ')) {
        segments.push(segment.charAt(0).toLocaleUpperCase(locale) + segment.slice(1));
      }

      return segments.join(' ');
    }

    const segmenter = new Intl.Segmenter(locale, { granularity: 'word' }).segment(result);

    for (const segment of segmenter) {
      let token = segment.segment as string;

      if (segment.isWordLike === true) {
        token = token.charAt(0).toLocaleUpperCase(locale) + token.slice(1);
      }

      segments.push(token);
    }

    return segments.join('');
  }

  /**
   * Uses the `Intl` API to derive the currency symbol from the currency code.
   *
   * @param currency The currency code.
   * @param locale The locale to use for the currency symbol.
   * @returns The currency symbol.
   */
  static getCurrencySymbol(currency: string, locale = defaultLocale): string {
    const result = new Intl.NumberFormat(locale, {
      currency,
      currencyDisplay: 'symbol',
      style: 'currency',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency');

    if (result != null) {
      return result.value;
    }

    return '';
  }

  /**
   * Uses the `Intl` API to derive the language name from the language code.
   *
   * @param language The language code.
   * @param locale The locale to use for the language name.
   * @returns The language name.
   */
  static getLanguageName(language: string, locale = defaultLocale): string {
    const result = new Intl.DisplayNames(locale, { type: 'language' }).of(language) ?? '';
    const segments = [];

    /**
     * Intl.Segmenter is not supported in the current Firefox release.
     */
    if (Intl.Segmenter === undefined) {
      for (const segment of result.split(' ')) {
        segments.push(segment.charAt(0).toLocaleUpperCase(locale) + segment.slice(1));
      }

      return segments.join(' ');
    }

    const segmenter = new Intl.Segmenter(locale, { granularity: 'word' }).segment(result);

    for (const segment of segmenter) {
      let token = segment.segment as string;

      if (segment.isWordLike === true) {
        token = token.charAt(0).toLocaleUpperCase(locale) + token.slice(1);
      }

      segments.push(token);
    }

    return segments.join('');
  }

  /**
   * Instantiates a new `Market` object from the given country code.
   *
   * @param country The country code.
   * @param fallback Optional country code to fallback to.
   * @returns The market object.
   */
  getMarketByCountry(country: CountryCodes, fallback?: CountryCodes | null): Market {
    try {
      const key = country.toLowerCase() as MarketKey;

      /**
       * We need to check if the country code matches because we also support aliased market keys.
       */
      if (this.#data[key]?.id === country) {
        return new Market(this.#data[key] as Country);
      }

      throw new Error(`Unable to derive market from ${country}.`);
    } catch (error) {
      if (fallback == null) {
        throw error;
      }

      return this.getMarketByCountry(fallback);
    }
  }

  /**
   * Instantiates a new `Market` object from the given URL segment.
   *
   * @param url A URL or segment with a alias or market-language[-country] segment.
   * @param fallback Optional country code to fallback to.
   * @returns The market object.
   */
  getMarketByURL(url?: URL | string, fallback?: CountryCodes | null): Market {
    try {
      if (url == null) {
        throw new Error('Unable to derive market from empty segment.');
      } else if (url instanceof URL) {
        url = url.pathname;
      } else if (/^https?:[/][/]/i.test(url) === true) {
        url = new URL(url).pathname;
      }

      url = url.split('/').find((value) => value.length > 0);

      if (url == null || url === '') {
        throw new Error('Unable to derive market from empty segment.');
      }

      const segments = url.split('-');

      /**
       * If the segment consists of a single token, it must be an alias.
       * This logic supports our legacy markets (GB, DE, FR, IE).
       */
      if (segments.length === 1) {
        const key = url.toLowerCase() as MarketKey;

        if (this.#data[key]?.alias === key) {
          return new Market(this.#data[key] as Country);
        }

        throw new Error(`Unable to derive market from '${url}' alias.`);
      } else if (segments.length === 3 && segments[0] === segments[2]) {
        /**
         * We don't want to serve the same market locale from two distinct URLs.
         */
        throw new Error(`Redundant country and locale variant segments in '${url}'.`);
      }

      /**
       * The first segment specifies the market, and the second specifies the language.
       * The third segment is optional, and specifies the language-country variant.
       */
      const [market, language, variant] = [
        segments.shift()?.toUpperCase() as CountryCodes,
        segments.shift(),
        segments.shift()?.toUpperCase() as CountryCodes | undefined,
      ];

      const key = market.toLowerCase() as Lowercase<CountryCodes>;
      const candidate = this.#data[key];

      /**
       * If we can't locate a country by the pre-computed country/alias market key then we should bail.
       */
      if (candidate == null || (candidate.alias ?? candidate.id).toUpperCase() !== market) {
        throw new Error(`Unable to derive market from '${url}'.`);
      }

      /**
       * If the variant is not explicitly provided, we assume it's the country variant.
       */
      const locale = [language, variant ?? candidate.id].filter((value) => value != null).join('-');
      const locales = candidate.alias != null ? candidate.locales.slice(1) : candidate.locales;

      if (locales.some((value) => value.locale === locale) !== true) {
        throw new Error(`Locale '${locale}' is not supported for ${candidate.id} market.`);
      }

      return new Market(candidate, locale);
    } catch (error) {
      if (fallback == null) {
        throw error;
      }

      return this.getMarketByCountry(fallback);
    }
  }

  getMarketWithLocale(country: CountryCodes, locale: string): Market {
    const key = country.toLowerCase() as MarketKey;
    const candidate = this.#data[key];

    if (candidate == null) {
      throw new Error(`Unable to derive market from ${country}.`);
    }

    /**
     * Check if combination of market and locale is allowed.
     */
    const locales = candidate.locales;

    /**
     * If only the language is provided, we need to construct the locale by appending the country code.
     */
    if (locale.length === 2) {
      locale = `${locale}-${country}`;
    }

    if (locales.some((value) => value.locale === locale) !== true) {
      throw new Error(`Locale '${locale}' is not supported for ${candidate.id} market.`);
    }

    return new Market(candidate, locale);
  }

  /**
   * Returns all markets that match the provided flags.
   *
   * @param flags Optional flags to filter by.
   * @returns All filtered markets.
   */
  getMarkets(flags?: Partial<Country['flags']>): Market[] {
    return this.getCountries(flags).map((country) => new Market(country));
  }

  /**
   * Returns the timezone offset in hours for the given timezone.
   *
   * @param timezone The timezone to get the offset for.
   * @returns The timezone offset in hours.
   */
  static getTimezoneOffsetHours(timezone: string): number {
    const timeZones = getTimeZones();
    const timezoneData = timeZones.find((timeZone) => timeZone.name === timezone);
    return (timezoneData?.currentTimeOffsetInMinutes ?? 0) / 60;
  }
}

export class Market {
  #data: Country;
  #locale?: string;
  #currency?: string;

  constructor(data: Country, locale?: string) {
    this.#data = data;
    this.#locale = locale;
  }

  /**
   * Returns the alias URL prefix that serves this market, if any.
   */
  get alias() {
    return this.#data.alias;
  }

  /**
   * Returns the list of admin area indexes for this market, ordered by most to least specific.
   */
  get areas(): Array<number> {
    return this.#data.areas;
  }

  /**
   * Returns the minimum and maximum wedding budget values for this market.
   * The absolute values are calculated as 5% to 295% of the average wedding cost.
   */
  get budgetRange() {
    const averageCost = this.config.averageCost ?? 0;

    return {
      min: averageCost * 0.05,
      max: averageCost * 2.95,
    };
  }

  get config() {
    return this.#data.config ?? {};
  }

  get country() {
    return this.#data.id;
  }

  get currency() {
    return this.#currency ?? this.#data.currency;
  }

  set currency(currency: string) {
    this.#currency = currency;
  }

  get flags() {
    return this.#data.flags;
  }

  get hasAnyDirectory() {
    return this.suppliers.length > 0;
  }

  get hasFullDirectory() {
    return this.suppliers.includes('venue') && this.suppliers.length > 1;
  }

  get hasPostalCode() {
    return this.config.postalCode !== undefined;
  }

  get hasVenueDirectory() {
    return this.suppliers.includes('venue');
  }

  get hasSupplierDirectory() {
    return this.suppliers.some((supplier) => supplier !== 'venue');
  }

  /**
   * Returns the base language for the current locale.
   * For example, the `en-GB` locale would return `en`.
   */
  get language() {
    return this.locale.split('-')[0];
  }

  /**
   * Returns the base languages for all locale.
   */
  get languages() {
    return [...new Set(this.locales.map((locale) => locale.split('-')[0]))];
  }

  /**
   * Returns the current locale or the default market locale if none is set.
   */
  get locale() {
    return this.#locale ?? this.locales[0];
  }

  /**
   * Sets the current locale.
   * This is useful for when user preferences are available.
   */
  set locale(locale: string) {
    this.#locale = locale;
  }

  /**
   * Returns the list of locales supported by this market, sorted by weight.
   */
  get locales() {
    return this.#data.locales.map((value) => value.locale);
  }

  /**
   * Returns the mappings for this market.
   */
  get mappings() {
    return this.#data.mappings;
  }

  /**
   * Returns the ID of the bucket for this market.
   */
  get bucket() {
    return this.#data.bucket?.id;
  }

  /**
   * Returns the canonical URL prefix that serves this market in the most specific locale allowed.
   */
  get prefix() {
    /**
     * Trim the country from the locale, iff the locale ends with the same country code as the market.
     */
    const pattern = new RegExp(`-${this.country}$`, 'i');

    /**
     * If an alias is defined for this market, we should always use it.
     */
    if (this.#data.alias != null) {
      /**
       * If the current locale is supported and it's not the default for this market, we should return it.
       */
      if (this.locales.slice(1).includes(this.locale) === true) {
        return `${this.#data.alias}-${this.locale.replace(pattern, '')}`.toLowerCase();
      }

      return this.#data.alias;
    }

    /**
     * If the current locale is not supported in this market, we should return the default country locale instead.
     */
    if (this.locales.includes(this.locale) !== true) {
      return `${this.country}-${this.locales[0].replace(pattern, '')}`.toLowerCase();
    }

    return `${this.country}-${this.locale.replace(pattern, '')}`.toLowerCase();
  }

  /**
   * Returns the settings for this market bucket, with overrides applied.
   */
  get settings() {
    if (this.#data.bucket?.id == null) {
      throw new Error(`Market ${this.#data.id} does not have a bucket assigned.`);
    }

    const result = buckets[this.#data.bucket.id];

    if (result == null) {
      throw new Error(`Market ${this.#data.id} has an invalid bucket assigned.`);
    }

    const override = overrides[this.#data.id] ?? {};
    return mergeBucketSettings(result.settings, override);
  }

  /**
   * Returns which supplier types are available in the directory for this market.
   */
  get suppliers() {
    return this.#data.suppliers || [];
  }

  /**
   * Returns an Emoji flag for the country.
   * Note that Emoji flags are not rendered correctly on Windows.
   * By default the country of the current market will be used.
   *
   * @param country The country code.
   * @returns The emoji flag.
   */
  getCountryFlagEmoji(country: CountryCodes | 'EU' = this.country) {
    return Gazetteer.getCountryFlagEmoji(country);
  }

  /**
   * Returns an URL to a flag image for the country.
   * The original files were downloaded from https://flagcdn.com/.
   * By default the country of the current market will be used.
   *
   * @param country The country code.
   * @returns The URL to the flag image.
   */
  getCountryFlagImageURL(country: CountryCodes | 'EU' = this.country): string | undefined {
    return Gazetteer.getCountryFlagImageURL(country);
  }

  /**
   * Uses the `Intl` API to derive the country name from the country code.
   * By default the country of the current market will be used.
   *
   * @param country The country code.
   * @returns The country name.
   */
  getCountryName(country: CountryCodes = this.country): string {
    return Gazetteer.getCountryName(country, this.locale);
  }

  /**
   * Uses the `Intl` API to derive the currency name from the currency code.
   * This is useful for when we need to display the currency name in the UI.
   * By default the currency of the current market will be used.
   *
   * @param currency The currency code.
   * @returns The currency name.
   */
  getCurrencyName(currency: string = this.currency): string {
    return Gazetteer.getCurrencyName(currency, this.locale);
  }

  /**
   * Uses the `Intl` API to derive the currency symbol using the current locale.
   * By default the currency of the current market will be used.
   *
   * @param currency The currency code.
   * @returns The currency symbol.
   */
  getCurrencySymbol(currency: string = this.currency) {
    return Gazetteer.getCurrencySymbol(currency, this.locale);
  }

  /**
   * Uses the `Intl` API to derive the language name from the language code.
   * By default the language of the current market will be used.
   *
   * @param language The language code.
   * @returns The language name.
   */
  getLanguageName(language: string = this.language): string {
    return Gazetteer.getLanguageName(language, this.locale);
  }

  /**
   * Returns the primary timezone for this market.
   *
   * @returns The timezone name.
   */
  getPrimaryTimezone(): string {
    const timeZones = getTimeZones();
    const primaryTimezone = timeZones.find((timeZone) => timeZone.countryCode === this.country);
    return primaryTimezone?.name ?? '';
  }

  /**
   * Returns the timezone offset in hours for the given timezone.
   *
   * @param timezone The timezone to get the offset for.
   * @returns The timezone offset in hours.
   */
  getTimezoneOffsetHours(timezone: string): number {
    const timeZones = getTimeZones();
    const timezoneData = timeZones.find((timeZone) => timeZone.name === timezone);
    return (timezoneData?.currentTimeOffsetInMinutes ?? 0) / 60;
  }

  /**
   * Returns the search radius for the specified supplier type on the current market.
   *
   * @param type The type of supplier to get the search radius for, optional.
   */
  getSearchRadius(type: ICountry_Supplier) {
    return this.#data.mappings.search.radius[type] ?? this.#data.mappings.search.radius._;
  }

  /**
   * Returns flags for the specified locale.
   *
   * @param locale The locale to fetch flags from, defaults to the current market locale.
   * @returns The flags for the specified locale.
   */
  getLocaleFlags(locale: string = this.locale) {
    return this.#data.locales.find((value) => value.locale === locale)?.flags;
  }

  /**
   * Returns a specific setting for this market bucket, with overrides applied.
   * This method returns the setting value as-is, without any supplier type filtering.
   *
   * @param setting The setting key to retrieve.
   * @returns The setting value, or null if not defined.
   */
  getSetting<K extends keyof IBucket['settings']>(setting: K): IBucket['settings'][K] {
    const settingValue = this.settings[setting];

    if (!isDefined(settingValue)) {
      return null;
    }

    // If setting uses SupplierTypesSettings, we can't return it without a type
    // because we can't verify if it's allowed
    if (this.isSupplierTypeSetting(settingValue)) {
      throw new Error(`Setting ${setting} uses SupplierTypesSettings but no type was provided.`);
    }

    // If setting uses SupplierTypeOrWildcardSettings, we can't return it without a type
    // because we need to check the type-specific boolean value
    if (this.isSupplierTypeOrWildcardSetting(settingValue)) {
      throw new Error(
        `Setting ${setting} uses SupplierTypeOrWildcardSettings but no type was provided.`,
      );
    }

    return settingValue;
  }

  /**
   * Returns a specific setting for this market bucket, with overrides applied,
   * as-is, without any supplier type filtering.
   *
   * @param setting The setting key to retrieve.
   * @returns The setting value, or null if not defined.
   */
  getSettingRaw<K extends keyof IBucket['settings']>(setting: K): IBucket['settings'][K] {
    return this.settings[setting];
  }

  /**
   * Returns a specific setting for this market bucket, with overrides applied,
   * filtered by supplier type. If the setting uses SupplierTypesSettings,
   * it will filter based on inclusion/exclusion rules.
   *
   * @param setting The setting key to retrieve.
   * @param type The supplier type to filter the setting for.
   * @returns The setting value, or null if filtered out or not defined.
   */
  getSettingForType<K extends keyof IBucket['settings']>(
    setting: K,
    type: ICountry_Supplier,
  ): GetSettingReturnType<K, ICountry_Supplier> {
    const settingValue = this.settings[setting];

    if (!isDefined(settingValue)) {
      return null as GetSettingReturnType<K, ICountry_Supplier>;
    }

    // Setting must use SupplierTypesSettings or SupplierTypeOrWildcardSettings when type is provided
    if (
      !this.isSupplierTypeSetting(settingValue) &&
      !this.isSupplierTypeOrWildcardSetting(settingValue)
    ) {
      throw new Error(
        `Setting ${setting} does not use SupplierTypesSettings or SupplierTypeOrWildcardSettings but a type was provided.`,
      );
    }

    // Setting uses SupplierTypeOrWildcardSettings (has direct supplier type keys)
    if (this.isSupplierTypeOrWildcardSetting(settingValue)) {
      // Before returning the wildcard '*' fallback value, check if this supplier type
      // is explicitly excluded. Some settings combine wildcard defaults with an exclusion
      // list (types.excluded) to apply the setting to most types except specific ones.
      // If the type is excluded, return null instead of the wildcard value.

      if (this.isSupplierTypeSetting(settingValue)) {
        const { types } = settingValue;
        const { excluded } = types;

        if (typeof excluded !== 'undefined' && typeRuleMatches(excluded, type)) {
          return null as GetSettingReturnType<K, ICountry_Supplier>;
        }
      }
      // Has direct supplier type keys
      // Resolve in order: exact type, then the 'suppliers' alias (non-venue/non-group), then '*'.
      const settingObj = settingValue as unknown as Record<string, unknown>;
      if (type in settingObj) {
        return settingObj[type] as GetSettingReturnType<K, ICountry_Supplier>;
      }
      if (isSupplierAliasType(type) && SUPPLIER_TYPE_ALIAS in settingObj) {
        return settingObj[SUPPLIER_TYPE_ALIAS] as GetSettingReturnType<K, ICountry_Supplier>;
      }
      if ('*' in settingObj) {
        return settingObj['*'] as GetSettingReturnType<K, ICountry_Supplier>;
      }
      // Type not found, return null
      return null as GetSettingReturnType<K, ICountry_Supplier>;
    }

    // Setting uses SupplierTypesSettings, check inclusion/exclusion
    if (this.isSupplierTypeSetting(settingValue)) {
      const { types } = settingValue;
      const { included, excluded } = types;

      const isIncluded = typeof included !== 'undefined' ? typeRuleMatches(included, type) : true;
      const isExcluded = typeof excluded !== 'undefined' && typeRuleMatches(excluded, type);

      if (!isIncluded || isExcluded) {
        return null as GetSettingReturnType<K, ICountry_Supplier>;
      }

      return settingValue as GetSettingReturnType<K, ICountry_Supplier>;
    }

    // This should never be reached due to the error check above, but TypeScript needs it
    return null as GetSettingReturnType<K, ICountry_Supplier>;
  }

  hasSetting(setting: keyof IBucket['settings'], type?: ICountry_Supplier): boolean {
    if (type !== undefined) {
      return this.getSettingForType(setting, type) !== null;
    }
    return this.getSetting(setting) !== null;
  }

  /**
   * Returns the minimum tier from `tier.minimum` / `tier.minimumByType` for a setting that uses
   * SupplierTierSettings.
   *
   * Returns {@link Number.NEGATIVE_INFINITY} when there is no applicable minimum (missing setting,
   * no `tier` object, or no `minimum` / `minimumByType` value for the resolved type).
   *
   * @param settingName The setting key to retrieve tier requirements for.
   * @param type Optional supplier type to get the specific minimum when `minimumByType` is set.
   */
  getSettingRequiredTier(settingName: keyof IBucket['settings'], type?: ICountry_Supplier): number {
    const setting = this.settings[settingName];

    if (!setting || !isObject(setting) || !('tier' in setting) || !setting.tier) {
      return Number.NEGATIVE_INFINITY;
    }

    const tierSettings = setting.tier as SupplierTierSettings['tier'];
    return (
      (type && tierSettings.minimumByType?.[type]) ??
      tierSettings.minimum ??
      Number.NEGATIVE_INFINITY
    );
  }

  /**
   * Formats a number, or a range of two numbers, into a currency string using the current locale.
   * By default the currency of the current market will be used.
   *
   * @param values The number, or tuple of numbers to format.
   * @param options The options to customize the number formatting.
   * @returns The formatted currency [range] string.
   */
  formatCurrency(values: number | [number, number], options?: Intl.NumberFormatOptions): string {
    options = {
      ...options,
      currency: options?.currency ?? this.currency,
      style: 'currency',
    };

    if (typeof values === 'number') {
      return values.toLocaleString(this.locale, options).replace(/\s/g, ' ');
    }

    const formatter = new Intl.NumberFormat(this.locale, options);

    if ('formatRange' in formatter && typeof formatter.formatRange === 'function') {
      return formatter.formatRange(...values).replace(/\s/g, ' ');
    }

    /**
     * Fallback to formatting each value separately if `formatRange` is not supported.
     */
    return values
      .map((value) => value.toLocaleString(this.locale, options).replace(/\s/g, ' '))
      .join(' - ');
  }

  /**
   * Formats a number, or a range of two numbers, into a compact currency string using the current locale.
   * By default the currency of the current market will be used.
   *
   * @param values The number, or tuple of numbers, to format.
   * @param options The options to customize the number formatting.
   * @returns The formatted currency [range] string.
   */
  formatCurrencyCompact(
    values: number | [number, number],
    options?: Intl.NumberFormatOptions,
  ): string {
    options = {
      ...options,
      compactDisplay: 'short',
      currency: options?.currency ?? this.currency,
      currencyDisplay: 'symbol',
      notation: 'compact',
      style: 'currency',
      useGrouping: false,
    };

    let result: string;

    if (typeof values === 'number') {
      result = values.toLocaleString(this.locale, options);
    } else {
      const formatter = new Intl.NumberFormat(this.locale, options);

      if ('formatRange' in formatter && typeof formatter.formatRange === 'function') {
        result = formatter.formatRange(...values);
      } else {
        /**
         * Fallback to formatting each value separately if `formatRange` is not supported.
         */
        result = values.map((value) => value.toLocaleString(this.locale, options)).join(' - ');
      }
    }

    /**
     * Normalise whitespace and ensure consistent uppercase 'K' for thousands,
     * as the compact suffix casing can vary across ICU data versions.
     */
    return result.replace(/\s/g, ' ').replace(/(\d)k\b/gi, '$1K');
  }

  /**
   * Formats a date using the current locale.
   *
   * @param value The date to format.
   * @param options The options to customize the date formatting.
   * @returns The formatted date string.
   */
  formatDate(value: Date, options?: Intl.DateTimeFormatOptions): string {
    return value
      .toLocaleDateString(this.locale, {
        ...options,
      })
      .replace(/\s/g, ' ');
  }

  /**
   * Formats a list of strings using the current locale.
   *
   * @param values The list of strings to format.
   * @param options The options to customize the list formatting.
   * @returns The formatted list string.
   */
  formatList(
    values: string[],
    options?: { type: 'conjunction' | 'disjunction' | 'unit'; style: 'long' | 'short' | 'narrow' },
  ): string {
    return new (Intl as any).ListFormat(this.locale, options).format(values).replace(/\s/g, ' ');
  }

  /**
   * Formats a list of strings using the current locale and "and"-based grouping.
   *
   * @param values The list of strings to format.
   * @returns The formatted list string.
   */
  formatListConjunction(values: string[]) {
    return this.formatList(values, { type: 'conjunction', style: 'long' }).replace(/\s/g, ' ');
  }

  /**
   * Formats a list of strings using the current locale and "or"-based grouping.
   *
   * @param values The list of strings to format.
   * @returns The formatted list string.
   */
  formatListDisjunction(values: string[]) {
    return this.formatList(values, { type: 'disjunction', style: 'long' }).replace(/\s/g, ' ');
  }

  /**
   * Formats a date using the current locale.
   *
   * @param value The date to format.
   * @param options The options to customize the date/time formatting.
   * @returns The formatted date/time string.
   */
  formatDateTime(value: Date, options?: Intl.DateTimeFormatOptions): string {
    return value
      .toLocaleString(this.locale, {
        ...options,
      })
      .replace(/\s/g, ' ');
  }

  /**
   * Formats a number, or a range of two numbers, using the current locale.
   *
   * @param values The number, or tuple of numbers, to format.
   * @param options The options to customize the number formatting.
   * @returns The formatted number [range] string.
   */
  formatNumber(values: number | [number, number], options?: Intl.NumberFormatOptions): string {
    options = {
      ...options,
    };

    if (typeof values === 'number') {
      return values.toLocaleString(this.locale, options).replace(/\s/g, ' ');
    }

    const formatter = new Intl.NumberFormat(this.locale, options);

    if ('formatRange' in formatter && typeof formatter.formatRange === 'function') {
      return formatter.formatRange(...values).replace(/\s/g, ' ');
    }

    /**
     * Fallback to formatting each value separately if `formatRange` is not supported.
     */
    return values
      .map((value) => value.toLocaleString(this.locale, options).replace(/\s/g, ' '))
      .join(' - ');
  }

  /**
   * Formats a number into an ordinal string using the current locale.
   *
   * Returns `one` if it should have the english ordinal suffix `st`.
   * Returns `two` if it should have the english ordinal suffix `nd`.
   * Returns `few` if it should have the english ordinal suffix `rd`.
   * Returns `other` if it should have the english ordinal suffix `th`.
   *
   * @param value The number to format.
   * @param options The options to customize the ordinal formatting.
   * @returns The ordinal suffix string.
   */
  formatOrdinal(value: number, options?: Intl.PluralRulesOptions): 'one' | 'two' | 'few' | 'other' {
    return new Intl.PluralRules(this.locale, {
      ...options,
      type: 'ordinal',
    })
      .select(value)
      .replace(/\s/g, ' ') as 'one' | 'two' | 'few' | 'other';
  }

  /**
   * Formats a number with an ordinal suffix using the current locale.
   *
   * @param value The number to format.
   * @param options The options to customize the ordinal formatting.
   * @returns The number with the ordinal suffix string.
   */
  formatOrdinalSuffix(value: number, options?: Intl.PluralRulesOptions): string {
    const ordinal = this.formatOrdinal(value, options);
    const languages: Record<string, { one?: string; two?: string; few?: string; other: string }> = {
      en: { one: 'st', two: 'nd', few: 'rd', other: 'th' },
      fr: { one: 'er', other: 'e' },
    };

    if (languages[this.language]?.[ordinal] != null) {
      return `${value}${languages[this.language][ordinal]}`;
    }

    return `${value}.`;
  }

  /**
   * Formats a date using the current locale.
   *
   * @param value The date to format.
   * @param options The options to customize the time formatting.
   * @returns The formatted time string.
   */
  formatTime(value: Date, options?: Intl.DateTimeFormatOptions): string {
    return value
      .toLocaleTimeString(this.locale, {
        ...options,
      })
      .replace(/\s/g, ' ');
  }

  /**
   * Returns if the market has the given supplier category in directory.
   *
   * @param category The category to check for presence.
   * @returns True if the market has the supplier category, false otherwise.
   */
  hasSupplierCategory(category: ICountry_Supplier) {
    return this.suppliers.includes(category);
  }

  /**
   * Returns true if market obtained from url uses default market locale.
   */
  isDefaultMarketLocale() {
    return this.locales[0] === this.locale;
  }

  /**
   * Validates if given string is a valid postal code for the country.
   *
   * @param value The postal code to validate.
   * @returns Whether the postal code is valid, or `null` if the country does not have postal codes.
   */
  isValidPostalCode(value: string): boolean | null {
    if (this.config.postalCode === undefined) {
      return null;
    }

    return new RegExp(`^(?:${this.config.postalCode})$`).test(value);
  }

  /**
   * Rearranges the list of `adminAreas` according to the order defined by the market.
   *
   * @param adminAreas The list of admin areas, from the standard Address struct.
   * @param limit The maximum number of admin areas to return, defaults to all.
   * @returns The list of admin areas in the expected order (most to least specific).
   */
  sortAdminAreas<T>(adminAreas: T[], limit = Infinity): T[] {
    const result: T[] = [];

    for (const index of this.areas) {
      result.push(adminAreas.at(index) as T);

      if (--limit === 0) {
        break;
      }
    }

    return result;
  }

  /**
   * @returns {string} JSON representation of the market object.
   */
  toJSON(): Partial<Market> {
    return {
      country: this.country,
      currency: this.currency,
      language: this.language,
      locale: this.locale,
      locales: this.locales,
      prefix: this.prefix,
    };
  }

  /**
   * @returns {string} A string representation of the market object.
   */
  toString(): string {
    return `${this.country}-${this.currency}-${this.language}-${this.locale}-${this.prefix}`;
  }

  /**
   * Type guard: check if setting uses SupplierTypesSettings
   */
  private isSupplierTypeSetting<K extends keyof IBucket['settings']>(
    value: IBucket['settings'][K],
  ): value is NonNullable<IBucket['settings'][K]> & SupplierTypesSettings {
    return (
      isObject(value) &&
      'types' in value &&
      isObject(value.types) &&
      ('included' in value.types || 'excluded' in value.types)
    );
  }

  /**
   * Type guard: check if setting has direct supplier type keys or '*' at root level
   * (e.g., templatedResponses, weddingFairs: { venue: {...}, '*': {...} })
   */
  private isSupplierTypeOrWildcardSetting<K extends keyof IBucket['settings']>(
    value: IBucket['settings'][K],
  ): value is NonNullable<IBucket['settings'][K]> & SupplierTypeOrWildcardSettings {
    if (!isObject(value)) {
      return false;
    }

    // Check for direct supplier type keys pattern
    // Check if object has any supplier type keys, the 'suppliers' alias, or '*' at root level
    const keys = Object.keys(value);
    return keys.some((key) => key === '*' || key === SUPPLIER_TYPE_ALIAS || isSupplierType(key));
  }
}

/**
 * Default Gazetteer instance.
 * This is to avoid having to load the JSON multiple times.
 *
 * @TODO: Figure out a better way to load the appropriate JSON dump.
 */
export default new Gazetteer((env.LIVE ? GazetteerProd : GazetteerDev) as unknown as Country[]);
